import { getBrowserLocale, bestFacebookLocaleFor } from '../utils/locales';
import Identity from '../sdk/identity';

/** @hidden */
const FB_SDK_ID = 'facebook-jssdk';

/** @hidden */
export const loadFacebookSDK = (fbLocale: string) => {
  return new Promise<string | void>((resolve, reject) => {
    if (!document.getElementById(FB_SDK_ID)) {
      const element = document.createElement('script');
      const parent = 'body';
      const attr = 'src';

      element.onload = function () {
        resolve(`https://connect.facebook.net/${fbLocale}/sdk.js`);
      };

      element.onerror = function () {
        reject('Error loading Facebook SDK');
      };

      element.async = true;
      element.id = FB_SDK_ID;
      element[attr] = `https://connect.facebook.net/${fbLocale}/sdk.js`;
      document[parent].appendChild(element);
    } else {
      resolve();
    }
  });
};

/**
 *
 * @param appId the appId given to you by Facebook associated with your developer account - optional if you'd like to pass in manually. If you do not pass an id and one is already configured for your organization, the SDK will use that one by default.
 * @param locale specify a locale to localize Facebook login prompts. If unspecified, the Identity SDK will attempt to use the user's browser locale settings to find a locale.
 * @param customButton specify true if you want to use a custom 'Login with Facebook' button. The button must pass `Identity.facebookSignOn()` as its click handler
 *
 * Loads and initializes the Facebook SDK in your application. Must be called before calling any Facebook functionality (Sign-on, etc.).
 */
export async function initFacebookLogin(
  appId?: string,
  locale?: string,
  customButton?: boolean
): Promise<void> {
  if (!appId && !Identity.configOptions) {
    await Identity.getConfig();
  }
  const fbAppId = appId
    ? appId
    : Identity.configOptions && Identity.configOptions.facebookAppId
    ? Identity.configOptions.facebookAppId.split(',')[0]
    : null;

  if (!fbAppId) {
    throw new Error(
      '[Arc Identity SDK] - Facebook App ID not found, please add one in settings or explicitly pass one to the initFacebookLogin method.'
    );
  } else {
    const userLocale = locale ? locale : getBrowserLocale();
    const fbLocale = bestFacebookLocaleFor(userLocale);
    await loadFacebookSDK(fbLocale);

    if (customButton) {
      Identity._useCustomFBButton = true;
    }

    window.FB.init({
      appId: fbAppId,
      autoLogAppEvents: true,
      xfbml: true,
      status: true,
      version: 'v20.0'
    });
  }
}

export default initFacebookLogin;
